/**
 * Unified Token Manager
 *
 * Replaces the dual token storage system (Redux + localStorage) with a single
 * source of truth that keeps both in sync automatically.
 */
import { Dispatch } from '@reduxjs/toolkit';
import { SCORPIUS_KEY } from '../actions/types';
import { updateTokensFromInterceptor } from '../actions/updateTokensFromInterceptor';
import { getTokenExpirationWithFallback } from './jwtUtils';
import { isUsableRefreshToken } from './tokens';

export interface TokenSet {
  token: string;
  refresh_token: string;
  tokenExpiration?: number;
  refreshExpiration?: number;
}

export interface TokenManagerConfig {
  dispatch?: Dispatch;
  onTokensChanged?: (tokens: TokenSet | null) => void;
}

class TokenManager {
  private dispatch?: Dispatch;
  private onTokensChanged?: (tokens: TokenSet | null) => void;
  private currentTokens: TokenSet | null = null;

  constructor(config: TokenManagerConfig = {}) {
    this.dispatch = config.dispatch;
    this.onTokensChanged = config.onTokensChanged;

    // Load initial tokens from localStorage
    this.currentTokens = this.loadFromStorage();
  }

  /**
   * Set Redux dispatch function (called when store is available)
   */
  setDispatch(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  /**
   * Get current tokens (replaces getTokens())
   */
  getTokens(): TokenSet | null {
    return this.currentTokens;
  }

  /**
   * Set tokens and sync to both Redux and localStorage
   */
  setTokens(tokens: TokenSet | null): void {
    this.currentTokens = tokens;

    // Always update localStorage first
    this.saveToStorage(tokens);

    // Update Redux if dispatch is available
    if (this.dispatch && tokens) {
      this.dispatch(updateTokensFromInterceptor(tokens));
    }

    // Call onChange callback
    this.onTokensChanged?.(tokens);
  }

  /**
   * Update tokens with automatic expiration calculation
   */
  updateTokens(partial: { token?: string; refresh_token?: string }): void {
    if (!partial.token && !partial.refresh_token) {
      return;
    }

    // Build new token set by merging provided tokens with existing ones
    const currentTokens = this.currentTokens ?? {
      token: '',
      refresh_token: '',
    };
    const newTokens: TokenSet = {
      token: partial.token ?? currentTokens.token,
      refresh_token: partial.refresh_token ?? currentTokens.refresh_token,
    };

    // Calculate expirations using JWT if available
    if (partial.token) {
      newTokens.tokenExpiration = getTokenExpirationWithFallback(
        partial.token,
        60,
      );
    } else {
      newTokens.tokenExpiration = this.currentTokens?.tokenExpiration;
    }

    if (partial.refresh_token) {
      newTokens.refreshExpiration = getTokenExpirationWithFallback(
        partial.refresh_token,
        90 * 24 * 60,
      );
    } else {
      newTokens.refreshExpiration = this.currentTokens?.refreshExpiration;
    }

    this.setTokens(newTokens);
  }

  /**
   * Clear all tokens
   */
  clearTokens(): void {
    this.setTokens(null);
  }

  /**
   * Check if access token exists and is valid
   */
  hasValidAccessToken(): boolean {
    const tokens = this.getTokens();
    if (!tokens?.token) return false;

    const expiration = tokens.tokenExpiration;
    if (!expiration) return true; // If no expiration, assume valid

    // Add 1 minute buffer
    return Date.now() + 60000 < expiration;
  }

  /**
   * Check if refresh token exists and is valid
   */
  hasValidRefreshToken(): boolean {
    const tokens = this.getTokens();
    if (!tokens || !isUsableRefreshToken(tokens.refresh_token)) {
      return false;
    }

    const expiration = tokens.refreshExpiration;
    if (!expiration) return true; // If no expiration, assume valid

    return Date.now() < expiration;
  }

  /**
   * Load tokens from localStorage
   */
  private loadFromStorage(): TokenSet | null {
    try {
      const tokenString = localStorage.getItem(SCORPIUS_KEY);
      if (!tokenString) return null;

      const parsed = JSON.parse(tokenString);

      // Validate the parsed tokens have the expected structure
      if (typeof parsed !== 'object' || parsed === null) {
        localStorage.removeItem(SCORPIUS_KEY);
        return null;
      }

      // Ensure it has token properties (allow empty strings but not undefined/null types)
      if (
        typeof parsed.token !== 'string' ||
        typeof parsed.refresh_token !== 'string'
      ) {
        localStorage.removeItem(SCORPIUS_KEY);
        return null;
      }

      return parsed as TokenSet;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse stored tokens:', error);
      localStorage.removeItem(SCORPIUS_KEY);
      return null;
    }
  }

  /**
   * Save tokens to localStorage
   */
  private saveToStorage(tokens: TokenSet | null): void {
    try {
      if (tokens) {
        localStorage.setItem(SCORPIUS_KEY, JSON.stringify(tokens));
      } else {
        localStorage.removeItem(SCORPIUS_KEY);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save tokens to localStorage:', error);
    }
  }
}

// Global singleton instance
let globalTokenManager: TokenManager | null = null;

/**
 * Get the global token manager instance
 */
export const getTokenManager = (): TokenManager => {
  if (!globalTokenManager) {
    globalTokenManager = new TokenManager();
  }
  return globalTokenManager;
};

/**
 * Initialize token manager with Redux dispatch
 */
export const initializeTokenManager = (dispatch: Dispatch): TokenManager => {
  const manager = getTokenManager();
  manager.setDispatch(dispatch);
  return manager;
};

/**
 * Construct an isolated TokenManager instance for use in tests.
 *
 * App code must use the getTokenManager() singleton; the TokenManager class is
 * intentionally not exported so it cannot be instantiated by accident. Tests,
 * however, need fresh instances with custom config (dispatch / onTokensChanged)
 * that do not share the global singleton's state. This factory provides that
 * seam with an explicit, greppable name.
 */
export const initializeTokenManagerForTesting = (
  config: TokenManagerConfig = {},
): TokenManager => new TokenManager(config);
